import React, { useState } from 'react';
import type { DashboardSection } from '../types';

// Import custom hooks
import { useAuthPermissions } from '../hooks/useAuthPermissions';
import { useDashboardData } from '../hooks/useDashboardData';
import { useCRUDOperations } from '../hooks/useCRUDOperations';

// Import section components
import { ExcelListsSection } from './sections/ExcelListsSection';
import { ActivitiesSection } from './sections/ActivitiesSection';
import { MudSection } from './sections/MudSection';
import { LGSCalculationsSection } from './sections/LGSCalculationsSection';
import { ApiMeshSection } from './sections/ApiMeshSection';
import { OperadorasSection } from './sections/OperadorasSection';
import { EquiposSection } from './sections/EquiposSection';
import { BombasPistonSection } from './sections/BombasPistonSection';
import { PadsSection } from './sections/PadsSection';
import { PozosSection } from './sections/PozosSection';
import { TramosSection } from './sections/TramosSection';
import { TrepanosSection } from './sections/TrepanosSection';
import { PiletasSection } from './sections/PiletasSection';
import { MaterialesSection } from './sections/MaterialesSection';
import { ServiciosSection } from './sections/ServiciosSection';
import { TablasSection } from './sections/TablasSection';
import { AccountRequestsSection } from './sections/AccountRequestsSection';
import { UsersSection } from './sections/UsersSection';

const DashboardOptimized: React.FC = () => {
  // Custom hooks
  const { user, loading, currentUserRole, userPermissions, migrationComplete, initializationError } = useAuthPermissions();

  // Main state
  const [seccion, setSeccion] = useState<DashboardSection>('piletas');

  // Dashboard data hook (using empty strings since these are managed by individual sections now)
  const dashboardData = useDashboardData('', '', '', '');

  // CRUD operations hook
  const crudOperations = useCRUDOperations(user);

  // Early returns for loading and auth states
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <h2>Cargando...</h2>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <h2>Por favor, inicia sesión para acceder al panel de administración</h2>
      </div>
    );
  }

  if (initializationError) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <h2>Error de Inicialización</h2>
        <p style={{ color: '#d32f2f', margin: '20px 0' }}>{initializationError}</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '10px 20px',
            backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Recargar Página
        </button>
      </div>
    );
  }

  if (!migrationComplete) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <h2>Inicializando Sistema...</h2>
        <div style={{ margin: '20px 0' }}>
          <div style={{
            width: '200px',
            height: '4px',
            backgroundColor: '#f0f0f0',
            margin: '0 auto',
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: '100%',
              height: '100%',
              backgroundColor: '#1976d2',
              animation: 'loading 2s ease-in-out infinite'
            }} />
          </div>
        </div>
        <p style={{ color: '#666' }}>Configurando base de datos y permisos...</p>
        <style>{`
          @keyframes loading {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
        `}</style>
      </div>
    );
  }

  // Main render
  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <h1 style={{ color: '#1976d2', marginBottom: '10px' }}>🔧 Panel de Administración - Solids Control</h1>
        <p style={{ color: '#666', fontSize: '16px' }}>
          Bienvenido {user.email} | Rol: {currentUserRole} |
          {userPermissions.canEditReports ? ' ✏️ Escritura' : ' 👁️ Solo lectura'}
          {userPermissions.canDeleteReports ? ' | 🗑️ Eliminación' : ''}
          {userPermissions.canManageUsers ? ' | 👥 Gestión usuarios' : ''}
        </p>
      </div>

      {/* Navigation Menu */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '15px',
        justifyContent: 'center',
        marginBottom: '40px',
        padding: '20px',
        backgroundColor: '#f8f9fa',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        {/* Core Entity Management */}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setSeccion('operadoras')}
            style={{
              padding: '12px 20px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              backgroundColor: seccion === 'operadoras' ? '#4CAF50' : undefined
            }}
          >
            🏢 Operadoras
          </button>

          <button
            onClick={() => setSeccion('equipos')}
            style={{
              padding: '12px 20px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              backgroundColor: seccion === 'equipos' ? '#4CAF50' : undefined
            }}
          >
            🔧 Equipos
          </button>

          <button
            onClick={() => setSeccion('bombas-piston')}
            style={{
              padding: '12px 20px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              backgroundColor: seccion === 'bombas-piston' ? '#4CAF50' : undefined
            }}
          >
            ⚙️ Bombas a pistón
          </button>
        </div>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setSeccion('pads')}
            style={{
              padding: '12px 18px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              backgroundColor: seccion === 'pads' ? '#4CAF50' : undefined
            }}
          >
            📍 Pads
          </button>

          <button
            onClick={() => setSeccion('pozos')}
            style={{
              padding: '12px 18px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              backgroundColor: seccion === 'pozos' ? '#4CAF50' : undefined
            }}
          >
            🕳️ Pozos
          </button>

          <button
            onClick={() => setSeccion('tramos')}
            style={{
              padding: '12px 18px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              backgroundColor: seccion === 'tramos' ? '#4CAF50' : undefined
            }}
          >
            📏 Tramos
          </button>

          <button
            onClick={() => setSeccion('trepanos')}
            style={{
              padding: '12px 18px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              backgroundColor: seccion === 'trepanos' ? '#4CAF50' : undefined
            }}
          >
            🔩 Trepanos
          </button>
        </div>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setSeccion('piletas')}
            style={{
              padding: '12px 18px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              backgroundColor: seccion === 'piletas' ? '#4CAF50' : undefined
            }}
          >
            🛢️ Piletas
          </button>

          <button
            onClick={() => setSeccion('tablas')}
            style={{
              padding: '12px 18px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              backgroundColor: seccion === 'tablas' ? '#4CAF50' : undefined
            }}
          >
            📊 Tablas
          </button>
        </div>

        {/* Reporting System */}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setSeccion('reportes')}
            style={{
              padding: '12px 18px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              backgroundColor: seccion === 'reportes' ? '#2196F3' : '#e0f2fe'
            }}
          >
            📈 Reportes
          </button>

          <button
            onClick={() => setSeccion('listas-excel')}
            style={{
              padding: '12px 18px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              backgroundColor: seccion === 'listas-excel' ? '#FF9800' : '#fff3e0'
            }}
          >
            📋 Listas Excel
          </button>

          <button
            onClick={() => setSeccion('actividades')}
            style={{
              padding: '12px 18px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              backgroundColor: seccion === 'actividades' ? '#9C27B0' : '#f3e5f5'
            }}
          >
            📝 Actividades
          </button>

          <button
            onClick={() => setSeccion('lodo')}
            style={{
              padding: '12px 18px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              backgroundColor: seccion === 'lodo' ? '#795548' : '#efebe9'
            }}
          >
            🧪 Lodo
          </button>

          <button
            onClick={() => setSeccion('calculos-lgs')}
            style={{
              padding: '12px 18px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              backgroundColor: seccion === 'calculos-lgs' ? '#0288d1' : '#e3f2fd'
            }}
          >
            🧮 Cálculos LGS
          </button>

          <button
            onClick={() => setSeccion('mallas-api')}
            style={{
              padding: '12px 18px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              backgroundColor: seccion === 'mallas-api' ? '#607D8B' : '#eceff1'
            }}
          >
            🔗 Mallas API
          </button>

          <button
            onClick={() => setSeccion('materiales')}
            style={{
              padding: '12px 18px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              backgroundColor: seccion === 'materiales' ? '#2196F3' : '#e3f2fd'
            }}
          >
            🧪 Materiales
          </button>

          <button
            onClick={() => setSeccion('servicios')}
            style={{
              padding: '12px 18px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              backgroundColor: seccion === 'servicios' ? '#9C27B0' : '#f3e5f5'
            }}
          >
            🔧 Servicios
          </button>
        </div>

        {/* User Management */}
        {userPermissions.canManageUsers && (
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setSeccion('usuarios')}
              style={{
                padding: '12px 20px',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                backgroundColor: seccion === 'usuarios' ? '#FF9800' : undefined,
                color: seccion === 'usuarios' ? 'white' : undefined
              }}
            >
              👥 Usuarios
            </button>
            <button
              onClick={() => setSeccion('account-requests')}
              style={{
                padding: '12px 20px',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                backgroundColor: seccion === 'account-requests' ? '#FF5722' : undefined,
                color: seccion === 'account-requests' ? 'white' : undefined
              }}
            >
              📋 Solicitudes de Acceso
            </button>
          </div>
        )}

        <button
          onClick={() => setSeccion('auditoria')}
          style={{
            padding: '12px 20px',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            backgroundColor: seccion === 'auditoria' ? '#9C27B0' : undefined,
            color: seccion === 'auditoria' ? 'white' : undefined
          }}
        >
          📜 Auditoría
        </button>
      </div>

      {/* Section Content */}
      {seccion === 'operadoras' && (
        <OperadorasSection
          data={dashboardData}
          operations={crudOperations}
          userPermissions={userPermissions}
        />
      )}

      {seccion === 'equipos' && (
        <EquiposSection
          data={dashboardData}
          operations={crudOperations}
          userPermissions={userPermissions}
        />
      )}

      {seccion === 'bombas-piston' && (
        <BombasPistonSection
          data={dashboardData}
          userPermissions={userPermissions}
        />
      )}

      {seccion === 'pads' && (
        <PadsSection
          data={dashboardData}
          operations={crudOperations}
          userPermissions={userPermissions}
        />
      )}

      {seccion === 'pozos' && (
        <PozosSection
          data={dashboardData}
          operations={crudOperations}
          userPermissions={userPermissions}
        />
      )}

      {seccion === 'tramos' && (
        <TramosSection
          data={dashboardData}
          operations={crudOperations}
          userPermissions={userPermissions}
        />
      )}

      {seccion === 'trepanos' && (
        <TrepanosSection
          data={dashboardData}
          operations={crudOperations}
          userPermissions={userPermissions}
        />
      )}

      {seccion === 'piletas' && (
        <PiletasSection
          data={dashboardData}
          operations={crudOperations}
          userPermissions={userPermissions}
        />
      )}

      {seccion === 'materiales' && (
        <MaterialesSection
          data={dashboardData}
          operations={crudOperations}
          userPermissions={userPermissions}
        />
      )}

      {seccion === 'servicios' && (
        <ServiciosSection
          data={dashboardData}
          operations={crudOperations}
          userPermissions={userPermissions}
        />
      )}

      {seccion === 'tablas' && (
        <TablasSection
          data={dashboardData}
          operations={crudOperations}
          userPermissions={userPermissions}
        />
      )}

      {seccion === 'listas-excel' && (
        <ExcelListsSection
          data={dashboardData}
          operations={crudOperations}
          userPermissions={userPermissions}
        />
      )}

      {seccion === 'actividades' && (
        <ActivitiesSection
          data={dashboardData}
          operations={crudOperations}
          userPermissions={userPermissions}
        />
      )}

      {seccion === 'lodo' && (
        <MudSection
          data={dashboardData}
          operations={crudOperations}
          userPermissions={userPermissions}
        />
      )}

      {seccion === 'calculos-lgs' && (
        <LGSCalculationsSection
          data={dashboardData}
          operations={crudOperations}
          userPermissions={userPermissions}
        />
      )}

      {seccion === 'mallas-api' && (
        <ApiMeshSection
          data={dashboardData}
          operations={crudOperations}
          userPermissions={userPermissions}
        />
      )}

      {seccion === 'account-requests' && (
        <AccountRequestsSection userEmail={user?.email || undefined} />
      )}

      {seccion === 'usuarios' && (
        <UsersSection
          userEmail={user?.email || undefined}
          empresas={dashboardData.empresas}
        />
      )}

      {seccion === 'auditoria' && (
        <>
          <h2>📜 Registro de Auditoría</h2>
          <p style={{ color: '#666', marginBottom: 20 }}>
            Próximamente: Visualización completa de logs de auditoría con filtros por usuario, acción y fecha.
          </p>
          <div style={{ padding: 20, backgroundColor: '#f5f5f5', borderRadius: 8 }}>
            <h3>Funcionalidades Planificadas:</h3>
            <ul style={{ textAlign: 'left', maxWidth: 600, margin: '0 auto' }}>
              <li>📊 Dashboard de actividad de usuarios</li>
              <li>🔍 Búsqueda y filtrado avanzado</li>
              <li>📈 Gráficos de actividad por período</li>
              <li>📄 Exportación de reportes de auditoría</li>
              <li>🔄 Historial de cambios por documento</li>
              <li>👥 Análisis de actividad por usuario</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default DashboardOptimized;
